import { Lightning, Launch, PlatformSettings, AppData } from "@lightningjs/sdk";
import { App } from "./App.js";

import "./lib/polyfills";
import "./lib/iap";
import theme from "./lib/theme";
import { STORAGE_KEYS } from "./lib/utils";

if (
    window.location.search.indexOf("platform=posttv") > -1 &&
    window.location.search.indexOf("android=true") === -1 &&
    window.location.href.indexOf("https://") > -1 &&
    window.location.href.indexOf("-es5") === -1
) {
    window.location.href = window.location.href.replace(/^(https:\/\/.*?)(-front)?\.(.*)$/, "$1-front-es5.$3");
}

// PostTV/Netgem Linux STB can provide a websocket signature via URL hash.
// Capture it BEFORE app/router initialization, then remove it from the URL so
// hash-based routing can work normally.
(() => {
    try {
        const search = window.location.search || "";
        const isPostTvLinux = search.indexOf("platform=posttv") > -1 && search.indexOf("android=true") === -1;
        if (!isPostTvLinux || window.location.href.indexOf("-es5") === -1) return;

        const initialHash = window.location.hash || "";
        const looksLikeWsSignature =
            initialHash.startsWith("#") && !initialHash.startsWith("#/") && initialHash.includes("=");
        // console.log("initialHash", initialHash);
        window.sessionStorage?.setItem("initialHash", initialHash);

        if (!looksLikeWsSignature) return;

        try {
            window.sessionStorage?.setItem(STORAGE_KEYS.posttvWsSignature, initialHash);
        } catch (e) {
            // ignore storage issues
        }

        // Remove the hash without triggering a navigation event.
        try {
            if (window.history && typeof window.history.replaceState === "function") {
                window.history.replaceState(null, document.title, window.location.href.split("#")[0]);
            }
        } catch (e) {
            // If replaceState is not available/allowed, do nothing (we still stored the signature).
        }
    } catch (e) {
        // no-op: never block app startup
    }
})();

export default function (
    appSettings: Lightning.Application.Options,
    platformSettings: PlatformSettings,
    appData: AppData
) {
    // inspector should be disabled on prod
    if (!platformSettings.inspector) {
        document.body.style.background = theme.colors.bg.primary;

        const styleTag = document.createElement("style");
        styleTag.innerHTML = `
          canvas, video {
            max-width: 100vw ${window.location.search.indexOf("platform=posttv") > -1 ? "!important" : ""};
            max-height: 100vh;
            min-height: 100vh;
            object-position: top;
          }
          canvas {
            object-fit: contain;
          }
          video {
            object-fit: contain;
	        object-position: center;
          }
        `;
        document.head.appendChild(styleTag);

        const ASPECT_RATIO = 16 / 9;
        function updateVideoMaxWidth() {
            const video = document.querySelector("video");
            if (!video) return;

            const containerHeight = window.innerHeight;
            const scaledWidth = Math.floor(containerHeight * ASPECT_RATIO);

            video.style.maxWidth = `${scaledWidth}px`;
        }

        function waitForVideoAndApply() {
            const video = document.querySelector("video");
            if (!video) {
                requestAnimationFrame(waitForVideoAndApply);
                return;
            }

            updateVideoMaxWidth();
            window.addEventListener("resize", updateVideoMaxWidth);
        }
        waitForVideoAndApply();
    }

    // TEMP DRM CHECK todo to remove later!
    function checkDRMSupport(keySystem: string, config: any) {
        if (!window.navigator || !window.navigator.requestMediaKeySystemAccess) {
            return Promise.reject(new Error("EME not supported in this browser."));
        }
        console.log(`Checking for support of ${keySystem}...`);
        return navigator.requestMediaKeySystemAccess(keySystem, config);
    }

    async function checkAllDRMs() {
        const baseConfig = [
            {
                initDataTypes: ["cenc"],
                audioCapabilities: [
                    {
                        contentType: 'audio/mp4;codecs="mp4a.40.2"' // AAC codec
                    }
                ]
            }
        ];

        const drmChecks = [
            { name: "Widevine", id: "com.widevine.alpha", config: baseConfig },
            { name: "PlayReady", id: "com.microsoft.playready", config: baseConfig },
            { name: "FairPlay", id: "com.apple.fps", config: baseConfig }
        ];
        let supportedKeySystem = null;
        console.log("Check DRM");
        for (const drm of drmChecks) {
            try {
                await checkDRMSupport(drm.id, drm.config);
                supportedKeySystem = drm.name;
                console.log(`SUCCESS: Found supported DRM system: ${drm.name}`);
            } catch (error) {
                console.log(`FAIL: ${drm.name} is not supported or failed the check.`);
            }
        }

        if (!supportedKeySystem) {
            console.warn("⚠️ No suitable DRM system was found for protected content.");
        }
    }
    // checkAllDRMs();

    // Mock Android.testPinCode for local development
    if (
        (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1") &&
        window.location.search.indexOf("platform=posttv") > -1 &&
        window.location.search.indexOf("android=true") > -1
    ) {
        (window as any).Android = (window as any).Android || {};
        (window as any).Android.testPinCode = (code: string) => code === "1111";
    }

    console.log("[launch url]", window.location.search);

    // TitanOS (Philips/Mediatek) ships a Mali GPU with very limited shared VRAM.
    // Loading the dashboard causes GPU OOM → WebGL context is killed by the driver
    // → Lightning crashes with "Cannot set properties of null (setting 'params')".
    // Mitigation 1: lower memoryPressure so Lightning GC's textures much more aggressively.
    // Mitigation 2: recover by reloading the app if the context is lost anyway.
    const isTitanOS =
        window.location.search.includes("platform=titanos") ||
        navigator.userAgent.includes("TitanOS");

    if (isTitanOS) {
        // Default is 24 MB; 8 MB forces eviction of off-screen textures much sooner.
        if (appSettings.stage) {
            (appSettings.stage as any).memoryPressure = 8 * 1024 * 1024;
        }
        // Smaller JPEG thumbnails = less VRAM per texture.
        if ((platformSettings as any).image) {
            (platformSettings as any).image.quality = 75;
        }
    }

    // Helper: reload the app, but cap retries to avoid an infinite reload loop.
    const _tryReload = (reason: string) => {
        console.log(`[App] Recovery triggered (${reason})`);
        try {
            const KEY = "webgl_rc";
            const count = parseInt(sessionStorage.getItem(KEY) || "0", 10);
            if (count < 3) {
                sessionStorage.setItem(KEY, String(count + 1));
                setTimeout(() => window.location.reload(), 1000);
            } else {
                console.log("[App] Too many recovery attempts – giving up");
            }
        } catch (_) {
            // sessionStorage unavailable; reload unconditionally.
            setTimeout(() => window.location.reload(), 1000);
        }
    };

    // Attach a webglcontextlost listener on the canvas that Lightning creates.
    // Calling preventDefault() signals the browser that we want to handle
    // restoration ourselves; the 1-second delayed reload is the actual recovery.
    setTimeout(() => {
        const canvas = document.querySelector("canvas");
        if (canvas) {
            canvas.addEventListener("webglcontextlost", (e: Event) => {
                e.preventDefault();
                _tryReload("webglcontextlost");
            });
        }
    }, 0);

    // Lightning's internal router crashes after WebGL context loss with the error
    // "Cannot set properties of null (setting 'params')".  This is an uncaught
    // exception that freezes the app.  Intercept it and reload instead.
    window.addEventListener("error", (e: ErrorEvent) => {
        if (e.message && e.message.includes("Cannot set properties of null (setting 'params')")) {
            _tryReload("lightning-router-crash");
        }
    });

    return Launch(App, appSettings, platformSettings, appData);
}
